import { artistHref } from '@/features/artists/artistHref'
import { sharePlaylist } from '@/features/sharing/ShareSheet'
import { songActions } from '@/components/songActions'
import { CollectionHeader, pageClassName } from '@/components/ui/Page'
import { ErrorNotice } from '@/components/ui/Feedback'
import { ArrowLeft, ListMusic, Play, Shuffle } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'

import { useDaylist, useKeepDaylist } from '@/api/hooks'
import { usePlayer } from '@/audio/store'
import { TrackRow } from '@/components/TrackRow'
import { Button, TrackListSkeleton, toast } from '@/components/ui'
import { useHomePlayback } from '@/features/home/useHomePlayback'
import { songKey } from '@/features/home/homeFeed'
import { AddToPlaylistDialog, type AddToPlaylistTarget } from '@/features/search/AddToPlaylistDialog'
import { describeError } from '@/features/search/describeError'
import { playDaylist, useDaylistPreparation } from './daylistPlayback'

export function DaylistPage() {
  const { id } = useParams()
  const number = Number(id)
  const valid = Number.isInteger(number) && number > 0
  const query = useDaylist(valid ? number : undefined)
  const keep = useKeepDaylist()
  const navigate = useNavigate()
  const playback = useHomePlayback('daylist')
  const preparing = useDaylistPreparation((state) => state.daylistId)
  const current = usePlayer((state) => state.current()?.trackId)
  const playing = usePlayer((state) => state.isPlaying)
  const [target, setTarget] = useState<AddToPlaylistTarget | null>(null)
  const daylist = query.data

  return (
    <div className={pageClassName('collection')}>
      <Link to="/" className="mb-4 inline-flex min-h-11 items-center gap-2 text-sm text-text-muted"><ArrowLeft className="h-4 w-4" aria-hidden="true" />Home</Link>
      {!valid || query.isError ? (
        <ErrorNotice title="Daylist unavailable" busy={query.isFetching} onRetry={valid ? () => void query.refetch() : undefined}>Return Home for today’s mix.</ErrorNotice>
      ) : query.isPending ? <TrackListSkeleton count={10} /> : daylist ? (
        <>
          <div className="mb-6">
            <CollectionHeader kind={new Date(`${daylist.date}T12:00:00`).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })} title={daylist.name}
              subtitle="A fresh mix daily. Add it to your playlists to keep it."
              metadata={`${daylist.entries.length} songs · ${daylist.entries.filter((entry) => entry.familiar).length} familiar · ${daylist.entries.filter((entry) => !entry.familiar).length} discoveries`} />
            {daylist.status === 'ready' ? <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={() => void playDaylist(daylist)} loading={preparing === daylist.id}><Play className="h-4 w-4 fill-current" aria-hidden="true" />Play</Button>
              <Button variant="secondary" disabled={preparing === daylist.id} onClick={() => void playDaylist(daylist, true)}><Shuffle className="h-4 w-4" aria-hidden="true" />Shuffle</Button>
              <Button variant="secondary" loading={keep.isPending} onClick={() => {
                if (daylist.saved_playlist_id) { void navigate(`/playlists/${daylist.saved_playlist_id}`); return }
                keep.mutate(daylist.id, { onSuccess: ({ playlist_id }) => {
                  toast.success('Added to playlists', 'These songs are now kept in your library.')
                  void navigate(`/playlists/${playlist_id}`)
                } })
              }}><ListMusic className="h-4 w-4" aria-hidden="true" />{daylist.saved_playlist_id ? 'Open playlist' : 'Add to playlists'}</Button>
              <Button variant="secondary" loading={keep.isPending} onClick={() => {
                if (daylist.saved_playlist_id) { sharePlaylist(daylist.saved_playlist_id); return }
                keep.mutate(daylist.id, { onSuccess: ({ playlist_id }) => sharePlaylist(playlist_id) })
              }}>Save as playlist &amp; share</Button>
            </div> : <p role="status" className="mt-4 text-sm text-text-muted">{daylist.message || 'Preparing your daily mix…'}</p>}
            {keep.isError ? <ErrorNotice>{describeError(keep.error, 'Could not add this mix to your playlists.')}</ErrorNotice> : null}
          </div>
          <ul>
            {daylist.entries.map(({ track, in_library }) => {
              const key = songKey(track)
              const error = playback.errors[key]
              return <li key={track.id}>
                <TrackRow title={track.title} artistName={track.artist_name} artistHref={artistHref(track)} coverUrl={track.artwork_url} durationMs={track.duration_ms}
                  isActive={current === track.id} isPlaying={current === track.id && playing} onPlay={() => void playback.act(track)}
                  menuItems={songActions({ playNext: () => void playback.act(track, 'next'), addToQueue: () => void playback.act(track, 'queue'), addToPlaylist: () => setTarget({ title: track.title, provider: track.provider, externalId: track.external_id, trackId: track.id }), addToLibrary: () => void playback.act(track, 'download'), inLibrary: in_library, busy: playback.busy[key] })} />
                {error ? <ErrorNotice busy={playback.busy[key]} onRetry={() => void playback.act(track, error.action)}>{error.message}</ErrorNotice> : null}
              </li>
            })}
          </ul>
        </>
      ) : null}
      {target ? <AddToPlaylistDialog key={`${target.provider}:${target.externalId}`} target={target} onOpenChange={(open) => { if (!open) setTarget(null) }} /> : null}
    </div>
  )
}
